import { SubmissionStepType } from "generated-graphql/graphql";
import { needsUnderstandAcknowledgment } from "hooks/submission/needsUnderstandAcknowledgment";
import { useCurrentUser } from "hooks/useCurrentUser";
import { stepperStepPath } from "hooks/useStepperNavigation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { Navigate, useParams } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { FrozenStepPanel } from "./FrozenStepPanel";

/**
 * Steps rendered from the frozen snapshot (read-only, snapshot-sourced) once the
 * submission is frozen. Details owns its read-only handling: it renders
 * submission metadata, which is not in the snapshot.
 */
export const SNAPSHOT_FROZEN_STEPS = new Set<SubmissionStepType>([
  SubmissionStepType.FacilityProfile,
  SubmissionStepType.RcraWasteActivity,
  SubmissionStepType.Contacts,
  SubmissionStepType.FilingNotifications,
  SubmissionStepType.StateFields,
  SubmissionStepType.Review,
]);

/**
 * Renders the real editor for a single stepper step, wiring the `submissionId`
 * and `facilityId` from the shell's context.
 *
 * When the submission is frozen, snapshot-backed sections render their read-only
 * snapshot panel instead of a live editor (no edit affordances or "mark complete
 * & continue" footers). Details handles its own read-only state internally, so
 * it always mounts its editor.
 */
export function StepRoute({ step }: { step: SubmissionStepType }) {
  const { submissionId, facilityId, isFrozen, submission } = useWasteStepper();
  const { user, isStaff } = useCurrentUser();
  const { tenantId = "" } = useParams<{ tenantId: string }>();

  if (isFrozen && SNAPSHOT_FROZEN_STEPS.has(step)) {
    return <FrozenStepPanel step={step} submissionId={submissionId} />;
  }

  // The shell only mounts its Outlet once the submission has resolved, but
  // guard anyway: with no submission the acknowledgment check below can't run,
  // and mounting an editor first would flash it before a redirect.
  if (!submission) {
    return null;
  }

  // Deep links would otherwise skip the acknowledgment the index route enforces.
  const steps = (submission?.stepperSteps ?? []).map(({ step: s }) => s);
  const myReviewer = (submission?.reviewers ?? []).find((r) => r.user.id === user?.id);
  if (
    step === SubmissionStepType.ReviewAndSign &&
    steps.includes(SubmissionStepType.Understand) &&
    needsUnderstandAcknowledgment({
      isStaff,
      isFrozen,
      status: submission?.status,
      reviewer: myReviewer,
    })
  ) {
    const understandSlug =
      WASTE_SUBMISSION_STEP_PRESENTATION[SubmissionStepType.Understand].routeSlug;
    return <Navigate to={stepperStepPath(tenantId, submissionId, understandSlug)} replace />;
  }

  // OVERVIEW has no `Editor` (it renders `StepperOverview` on the index route),
  // so it never reaches `StepRoute`; the guard keeps the type honest.
  const { Editor } = WASTE_SUBMISSION_STEP_PRESENTATION[step];
  if (!Editor) {
    return null;
  }
  return <Editor submissionId={submissionId} facilityId={facilityId} />;
}
